'use client';

import { useEffect, useState } from 'react';
import { Globe, ArrowLeft, HelpCircle } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { getSaaSHomeUrl } from '@/utils/preLoginHome';

export default function DomainNotFoundPage() {
    const router = useRouter();
    // This page is only ever reached ON an unresolvable custom domain, so a
    // relative '/' is an infinite loop: the root route fails to resolve the
    // host and redirects straight back here. The way out has to be an
    // ABSOLUTE url to the Kaympus domain — the one genuinely cross-domain
    // link in the whole pre-login area.
    const [saasHome, setSaasHome] = useState('');
    useEffect(() => {
        const url = getSaaSHomeUrl();
        // '/' means the base domain is unknown at build time; better to show
        // no button than one that lands the visitor back on this page.
        setSaasHome(url === '/' ? '' : url);
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4">
            <div className="max-w-md w-full text-center space-y-8">
                {/* Visual Icon */}
                <div className="relative inline-block">
                    <div className="bg-blue-50 p-8 rounded-[2.5rem] shadow-sm animate-pulse">
                        <Globe size={64} className="text-blue-500" />
                    </div>
                    <div className="absolute -bottom-2 -right-2 bg-amber-100 p-3 rounded-2xl border-4 border-white shadow-lg">
                        <HelpCircle size={24} className="text-amber-600" />
                    </div>
                </div>

                {/* Content */}
                <div className="space-y-4">
                    <h1 className="text-4xl font-black text-gray-900 tracking-tight leading-tight">
                        Domain Not Linked
                    </h1>
                    <p className="text-gray-500 text-lg leading-relaxed">
                        This domain has pointed to our servers, but it hasn't been connected to a school portal yet.
                    </p>
                </div>

                {/* Instruction Card */}
                <div className="bg-gray-50 rounded-3xl p-8 border border-gray-100 text-left space-y-4">
                    <h2 className="font-bold text-gray-800 text-sm uppercase tracking-widest">How to fix this?</h2>
                    <ul className="space-y-4 text-sm text-gray-600">
                        <li className="flex gap-3">
                            <span className="bg-blue-100 text-blue-600 w-6 h-6 rounded-lg flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">1</span>
                            <span>Login to your <strong>School Management Portal</strong> using your default subdomain.</span>
                        </li>
                        <li className="flex gap-3">
                            <span className="bg-blue-100 text-blue-600 w-6 h-6 rounded-lg flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">2</span>
                            <span>Go to <strong>Settings &gt; Custom Domain</strong> and add this domain name.</span>
                        </li>
                        <li className="flex gap-3">
                            <span className="bg-blue-100 text-blue-600 w-6 h-6 rounded-lg flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">3</span>
                            <span>Click <strong>Verify & Activate SSL</strong> to link it permanently.</span>
                        </li>
                    </ul>
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-3">
                    {saasHome && (
                        <a
                            href={saasHome}
                            className="w-full py-4 rounded-2xl bg-primary-background text-white font-bold text-center hover:opacity-90 transition-opacity"
                            data-testid="domain-not-found-main-site-link"
                        >
                            Visit Main Website
                        </a>
                    )}
                    <button
                        onClick={() => router.back()}
                        className="flex items-center justify-center gap-2 text-gray-400 hover:text-gray-900 transition-colors text-sm font-bold"
                    >
                        <ArrowLeft size={16} />
                        Go Back
                    </button>
                </div>

                {/* Footer */}
                <div className="pt-8 border-t border-gray-100">
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-[0.2em]">
                        &copy; {new Date().getFullYear()} Kaympus by Kayease
                    </p>
                </div>
            </div>
        </div>
    );
}
